import { Check, FileText, Image as ImageIcon, File as FileIcon, PenTool } from 'lucide-react';

import type { FormQuestion } from '@kushya/shared';
import { cn } from '@/lib/utils';

interface AnswerSummaryProps {
  questions: FormQuestion[];
  answers: Record<string, unknown>;
  className?: string;
}

interface FileMetadata {
  fileKey: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  url: string;
}

interface SignatureValue {
  url: string;
  typedName?: string;
}

interface OptionAnswer {
  optionId?: string;
  optionIds?: string[];
}

type ChoiceQuestion = Extract<
  FormQuestion,
  { type: 'multiple_choice_single' | 'multiple_choice_multi' }
>;

function isChoiceQuestion(q: FormQuestion): q is ChoiceQuestion {
  return q.type === 'multiple_choice_single' || q.type === 'multiple_choice_multi';
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return <ImageIcon className="size-3.5" />;
  if (mimeType.includes('pdf')) return <FileText className="size-3.5" />;
  return <FileIcon className="size-3.5" />;
}

/**
 * Format an answer value for display based on question type
 */
function formatAnswer(question: FormQuestion, value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground italic">Skipped</span>;
  }

  // Multiple choice - resolve option labels
  if (isChoiceQuestion(question)) {
    const answer = value as OptionAnswer;
    const options = question.options ?? [];

    if (answer.optionId) {
      const option = options.find((o) => o.id === answer.optionId);
      return option?.label ?? answer.optionId;
    }

    if (answer.optionIds && Array.isArray(answer.optionIds)) {
      const labels = answer.optionIds
        .map((id) => options.find((o) => o.id === id)?.label ?? id)
        .join(', ');
      return labels || <span className="text-muted-foreground italic">None selected</span>;
    }

    // Fallback for string value (direct label match)
    if (typeof value === 'string') {
      return value;
    }

    return <span className="text-muted-foreground italic">Unknown selection</span>;
  }

  // File upload
  if (question.type === 'file_upload') {
    const files = Array.isArray(value) ? (value as FileMetadata[]) : [value as FileMetadata];
    const validFiles = files.filter((f) => f && typeof f === 'object' && 'fileName' in f);

    if (validFiles.length === 0) {
      // Legacy: might be a plain URL string
      if (typeof value === 'string' && value.startsWith('http')) {
        return (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            View file
          </a>
        );
      }
      return <span className="text-muted-foreground italic">No file</span>;
    }

    return (
      <div className="space-y-1.5">
        {validFiles.map((file, idx) => (
          <a
            key={file.fileKey || idx}
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-primary hover:underline"
          >
            {getFileIcon(file.mimeType)}
            <span className="truncate">{file.fileName}</span>
            <span className="shrink-0 text-xs text-muted-foreground">
              ({formatFileSize(file.fileSize)})
            </span>
          </a>
        ))}
      </div>
    );
  }

  // Signature
  if (question.type === 'signature') {
    const sig = value as SignatureValue;
    if (sig.url) {
      return (
        <div className="flex items-center gap-2">
          <PenTool className="size-3.5 text-muted-foreground" />
          <a
            href={sig.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            View signature
          </a>
          {sig.typedName && (
            <span className="text-muted-foreground">({sig.typedName})</span>
          )}
        </div>
      );
    }
    // Legacy: plain URL string
    if (typeof value === 'string' && value.startsWith('http')) {
      return (
        <div className="flex items-center gap-2">
          <PenTool className="size-3.5 text-muted-foreground" />
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            View signature
          </a>
        </div>
      );
    }
    return <span className="text-muted-foreground italic">No signature</span>;
  }

  // Text, email, phone - simple string display
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  // Fallback for complex objects
  try {
    return JSON.stringify(value);
  } catch {
    return <span className="text-muted-foreground italic">Unable to display</span>;
  }
}

/**
 * Displays a structured summary of all answered questions.
 * Shows question titles with their formatted answers.
 */
export function AnswerSummary({ questions, answers, className }: AnswerSummaryProps) {
  // Filter to questions that have answers
  const answeredQuestions = questions.filter(
    (q) => answers[q.ref] !== undefined && answers[q.ref] !== null
  );

  if (answeredQuestions.length === 0) {
    return (
      <div className={cn('text-center text-sm text-muted-foreground py-4', className)}>
        No answers recorded
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {answeredQuestions.map((question) => (
        <div key={question.ref} className="flex gap-3">
          <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <Check className="size-3 text-green-600 dark:text-green-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">{question.title}</p>
            <div className="mt-1 text-sm text-muted-foreground">
              {formatAnswer(question, answers[question.ref])}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
